import { kstDateRangeDays } from '@shared/utils/kstTime.util';

import { BugoStatFevent } from './adminBugoStat.types';
import { brandKeyOf, brandNameOfFevent, coffinOutDayKey } from './adminBugoStat.util';
import { OrderStatKind, OrderStatOrder } from './adminOrderStat.types';
import { isCanceledOrder, netAmountOf, netQuantityOf } from './adminOrderStat.util';
import {
  PenetrationBase,
  PenetrationBrandRow,
  PenetrationDayRow,
  PenetrationDrillDown,
  PenetrationFeventRow,
  PenetrationResult,
} from './adminPenetration.types';
import { BrandNameResolver } from './analyticsBrandName';

//* ── 지표 정의 ──────────────────────────────────────────────────────────────
//* 분모(발인 상가) : 발인일이 조회 기간 안에 있는 fevent 1건.
//*                   isDeleted는 절대 필터하지 않는다(발인 뒤 자동으로 붙는 플래그라
//*                   필터하면 과거 기간이 통째로 비어 보인다).
//* 분자(구매 상가) : 그 상가에 유효 주문이 1건 이상 붙은 경우.
//*                   답례품 전체취소 건은 "샀다"로 치지 않는다.
//* 침투율          : 구매 상가 ÷ 발인 상가.
//* 공유 대비       : 구매 상가(공유한 곳만) ÷ 공유 상가.
//*                   부고를 안 보낸 상가엔 주문이 들어올 길이 없어서, 실제로 놓친
//*                   기회는 이쪽이 더 정확하다. 침투율이 낮을 때 공유 문제인지
//*                   구매 문제인지 여기서 갈린다.
//*
//* ⚠️ 주문↔상가 연결은 주문의 fevent _id로만 한다.
//*    주문 자체의 bugoBrand가 아니라 **상가의 bugoBrand**로 묶어야 분모와 분자가
//*    같은 축에서 나뉜다 (주문 문서의 브랜드가 상가와 어긋난 옛 데이터가 있다).
//*
//* ⚠️ 주문은 발인 기간보다 넓은 창에서 끌어온다(useAdminPenetration.orderWindowOf).
//*    화환은 발인 전에, 답례품은 발인 후에 결제되기 때문. 창 밖 주문은 상가가
//*    조회 집합에 없어서 자동으로 빠진다.

const feventIdOf = (order: OrderStatOrder): string | null => {
  const fevent = order.fevent;
  if (!fevent) return null;
  return typeof fevent === 'string' ? fevent : fevent._id;
};

const shareCountOf = (fevent: BugoStatFevent): number => {
  const raw = fevent.meta?.shareCount;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 0;
};

/** 침투율 집계에 넣을 유효 주문인지 — 답례품 전체취소는 구매로 치지 않는다 */
export const isBuyingOrder = (order: OrderStatOrder, kind: OrderStatKind): boolean =>
  !(kind === 'gift' && isCanceledOrder(order));

/**
 * 상가별 행 만들기 — 발인 상가 하나에 그 상가의 주문을 모아 붙인다.
 * 집계(aggregatePenetration)와 드릴다운 목록이 같은 배열을 쓰도록 한 단계로 뽑는다.
 */
export const buildPenetrationRows = (
  fevents: BugoStatFevent[],
  orders: OrderStatOrder[],
  kind: OrderStatKind,
  filters: { brandKeys: string[] },
  resolveBrandName?: BrandNameResolver,
): { rows: PenetrationFeventRow[]; orphanOrderCount: number } => {
  const rowMap = new Map<string, PenetrationFeventRow>();

  fevents.forEach((fevent) => {
    const brandKey = brandKeyOf(fevent);
    if (filters.brandKeys.length > 0 && !filters.brandKeys.includes(brandKey)) return;
    rowMap.set(fevent._id, {
      fevent,
      coffinOutDate: coffinOutDayKey(fevent),
      brandKey,
      brandName: brandNameOfFevent(fevent, resolveBrandName),
      shareCount: shareCountOf(fevent),
      orders: [],
      orderCount: 0,
      amount: 0,
      quantity: 0,
    });
  });

  let orphanOrderCount = 0;

  orders.forEach((order) => {
    if (!isBuyingOrder(order, kind)) return;
    const feventId = feventIdOf(order);
    if (!feventId) {
      //* 상가 참조가 아예 없는 주문 — 어느 상가 몫인지 알 수 없다
      orphanOrderCount += 1;
      return;
    }
    const row = rowMap.get(feventId);
    //* 조회 집합에 없는 상가(발인일이 기간 밖 / 상조 필터에서 빠짐)는 정상적으로 제외
    if (!row) return;

    row.orders.push(order);
    row.orderCount += 1;
    row.amount += netAmountOf(order, kind);
    row.quantity += netQuantityOf(order, kind);
  });

  return { rows: Array.from(rowMap.values()), orphanOrderCount };
};

const emptyRow = (key: string, brandName: string): PenetrationBrandRow => ({
  key,
  brandName,
  feventCount: 0,
  sharedFeventCount: 0,
  buyingFeventCount: 0,
  sharedBuyingFeventCount: 0,
  orderCount: 0,
  amount: 0,
  quantity: 0,
  sharedOrderCount: 0,
  sharedAmount: 0,
  sharedQuantity: 0,
});

const addRow = (target: PenetrationBrandRow, row: PenetrationFeventRow): void => {
  const shared = row.shareCount > 0;
  const buying = row.orderCount > 0;

  target.feventCount += 1;
  if (shared) target.sharedFeventCount += 1;
  if (buying) target.buyingFeventCount += 1;
  if (shared && buying) target.sharedBuyingFeventCount += 1;
  target.orderCount += row.orderCount;
  target.amount += row.amount;
  target.quantity += row.quantity;
  //* 공유 분모용 — 공유한 상가의 주문만 따로 쌓는다
  if (shared) {
    target.sharedOrderCount += row.orderCount;
    target.sharedAmount += row.amount;
    target.sharedQuantity += row.quantity;
  }
};

const emptyDay = (date: string): PenetrationDayRow => ({
  date,
  feventCount: 0,
  buyingFeventCount: 0,
  sharedFeventCount: 0,
  sharedBuyingFeventCount: 0,
  orderCount: 0,
  amount: 0,
  sharedOrderCount: 0,
  sharedAmount: 0,
});

/** 합계 행에서 그룹 식별용 필드를 떼어낸다 */
const totalsOf = (row: PenetrationBrandRow): PenetrationResult['totals'] => ({
  feventCount: row.feventCount,
  sharedFeventCount: row.sharedFeventCount,
  buyingFeventCount: row.buyingFeventCount,
  sharedBuyingFeventCount: row.sharedBuyingFeventCount,
  orderCount: row.orderCount,
  amount: row.amount,
  quantity: row.quantity,
  sharedOrderCount: row.sharedOrderCount,
  sharedAmount: row.sharedAmount,
  sharedQuantity: row.sharedQuantity,
});

/**
 * 선택한 분모(base)에 맞는 (모수, 구매) 상가 수.
 * 표·엑셀·요약 카드가 같은 규칙을 쓰도록 여기 한 곳에서만 낸다.
 */
export const denomOf = (
  row: Pick<
    PenetrationBrandRow,
    'feventCount' | 'sharedFeventCount' | 'buyingFeventCount' | 'sharedBuyingFeventCount'
  >,
  base: PenetrationBase,
): { total: number; buying: number } =>
  base === 'shared'
    ? { total: row.sharedFeventCount, buying: row.sharedBuyingFeventCount }
    : { total: row.feventCount, buying: row.buyingFeventCount };

/**
 * 분모에 맞는 금액·주문수·수량.
 * '공유 상가' 분모일 땐 공유 상가에 붙은 주문만 세야 드릴다운 목록 합계와 맞는다.
 */
export const metricsOf = (
  row: {
    orderCount: number;
    amount: number;
    quantity?: number;
    sharedOrderCount: number;
    sharedAmount: number;
    sharedQuantity?: number;
  },
  base: PenetrationBase,
): { orderCount: number; amount: number; quantity: number } =>
  base === 'shared'
    ? {
        orderCount: row.sharedOrderCount,
        amount: row.sharedAmount,
        quantity: row.sharedQuantity ?? 0,
      }
    : { orderCount: row.orderCount, amount: row.amount, quantity: row.quantity ?? 0 };

/**
 * 상가별 행 → 상조별·발인일별 집계.
 * `days`는 조회 기간 전체를 빈 날 포함해 채운다(추이가 끊기지 않도록).
 */
export const aggregatePenetration = (
  rows: PenetrationFeventRow[],
  orphanOrderCount: number,
  period: { from: string; to: string },
): PenetrationResult => {
  const brandMap = new Map<string, PenetrationBrandRow>();
  const dayMap = new Map<string, PenetrationDayRow>();
  const totals = emptyRow('', '');

  rows.forEach((row) => {
    let brand = brandMap.get(row.brandKey);
    if (!brand) {
      brand = emptyRow(row.brandKey, row.brandName);
      brandMap.set(row.brandKey, brand);
    }
    addRow(brand, row);
    addRow(totals, row);

    if (row.coffinOutDate) {
      let day = dayMap.get(row.coffinOutDate);
      if (!day) {
        day = { ...emptyDay(row.coffinOutDate) };
        dayMap.set(row.coffinOutDate, day);
      }
      const shared = row.shareCount > 0;
      const buying = row.orderCount > 0;
      day.feventCount += 1;
      if (buying) day.buyingFeventCount += 1;
      if (shared) day.sharedFeventCount += 1;
      if (shared && buying) day.sharedBuyingFeventCount += 1;
      day.orderCount += row.orderCount;
      day.amount += row.amount;
      if (shared) {
        day.sharedOrderCount += row.orderCount;
        day.sharedAmount += row.amount;
      }
    }
  });

  //* 발인 상가가 많은 상조부터 — 침투율만으로 세우면 상가 2곳짜리가 위로 온다
  const brands = Array.from(brandMap.values()).sort(
    (a, b) => b.feventCount - a.feventCount || a.brandName.localeCompare(b.brandName),
  );

  const days: PenetrationDayRow[] = kstDateRangeDays(period.from, period.to).map(
    (date) => dayMap.get(date) ?? emptyDay(date),
  );

  return { totals: totalsOf(totals), brands, days, orphanOrderCount };
};

/** 드릴다운 조건에 맞는 상가 목록 */
export const drillDownPenetrationRows = (
  rows: PenetrationFeventRow[],
  drill: PenetrationDrillDown,
): PenetrationFeventRow[] =>
  rows
    .filter((row) => {
      if (drill.brandKey !== undefined && row.brandKey !== drill.brandKey) return false;
      if (drill.date !== undefined && row.coffinOutDate !== drill.date) return false;
      if (drill.dateFrom !== undefined || drill.dateTo !== undefined) {
        const day = row.coffinOutDate;
        //* 발인일이 없는 상가는 구간에 넣을 근거가 없다
        if (!day) return false;
        if (drill.dateFrom !== undefined && day < drill.dateFrom) return false;
        if (drill.dateTo !== undefined && day > drill.dateTo) return false;
      }
      if (drill.buying === 'buying' && row.orderCount === 0) return false;
      if (drill.buying === 'idle' && row.orderCount > 0) return false;
      //* 분모를 공유 기준으로 볼 땐 목록도 같은 모수여야 숫자가 맞는다
      if (drill.sharedOnly && row.shareCount === 0) return false;
      return true;
    })
    //* 금액 큰 상가부터 → 같은 금액이면 발인일 순
    .sort(
      (a, b) =>
        b.amount - a.amount ||
        (a.coffinOutDate ?? '').localeCompare(b.coffinOutDate ?? ''),
    );
